import { localeTag, type Dict, type Lang, type PluralForms } from "@/lib/i18n";

// `Module.price` / `SubscriptionPlanPrice.price` are stored in CENTS (Stripe-native),
// even though the schema types `price` as Float. Pass raw cents — never ×100 first.
export function formatPrice(cents: number, currency = "EUR"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `€${(cents / 100).toFixed(2)}`;
  }
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

/**
 * Absolute date in the READER's locale.
 *
 * `lang` is required on purpose. This used to pass `undefined` as the locale,
 * which resolves to the *runtime's* default — the Node container's, on a server
 * render — so every date on the site came out in English no matter which
 * language the page was in ("Aug 28, 2026" on a Slovenian news card). Making the
 * argument required is what forces each call site to say whose locale it means;
 * an optional one would have let the bug quietly survive at whichever site
 * forgot. `formatRelativeDate` below already took `lang`, so this only brings
 * its sibling in line.
 */
export function formatDate(value: string | null | undefined, lang: Lang): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(localeTag(lang), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Relative "added …" stamp for latest-content rows ("pred 2 dnevoma" /
 * "2 days ago"), via Intl.RelativeTimeFormat so Slovenian dual/plural forms
 * come out right in every runtime locale. Beyond ~a month the absolute date
 * reads better than "8 weeks ago", so it falls back to formatDate — which also
 * caps how stale the stamp can drift inside an ISR cache window.
 */
export function formatRelativeDate(value: string | null | undefined, lang: Lang): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  if (Math.abs(days) > 31) return formatDate(value, lang);
  try {
    const rtf = new Intl.RelativeTimeFormat(localeTag(lang), { numeric: "auto" });
    if (Math.abs(days) >= 7) return rtf.format(Math.trunc(days / 7), "week");
    if (days !== 0) return rtf.format(days, "day");
    const hours = Math.round((d.getTime() - Date.now()) / 3_600_000);
    return hours === 0 ? rtf.format(0, "day") : rtf.format(hours, "hour");
  } catch {
    return formatDate(value, lang);
  }
}

/**
 * CLDR-correct "<n> <unit>" line for any runtime locale (O8). The plural
 * category comes from Intl.PluralRules (Slovenian one/two/few/other — ednina,
 * dvojina, množina nom./gen. — English & German one/other), and the noun
 * forms come from the Dict (`t.units.*`), so they're admin-translatable per
 * locale like every other chrome string.
 */
export function formatCount(n: number, lang: Lang, forms: PluralForms): string {
  let category: string;
  try {
    category = new Intl.PluralRules(localeTag(lang)).select(n);
  } catch {
    category = "other";
  }
  return `${n} ${(forms as Record<string, string>)[category] ?? forms.other}`;
}

export function formatCourseCount(n: number, lang: Lang, units: Dict["units"]): string {
  return formatCount(n, lang, units.course);
}

export function formatLessons(n: number, lang: Lang, units: Dict["units"]): string {
  return formatCount(n, lang, units.lesson);
}

export function formatSections(n: number, lang: Lang, units: Dict["units"]): string {
  return formatCount(n, lang, units.section);
}

export function formatEpisodes(n: number, lang: Lang, units: Dict["units"]): string {
  return formatCount(n, lang, units.episode);
}

export function formatItems(n: number, lang: Lang, units: Dict["units"]): string {
  return formatCount(n, lang, units.item);
}

export function formatInterval(interval: string): string {
  switch (interval) {
    case "month":
      return "month";
    case "year":
      return "year";
    case "week":
      return "week";
    case "day":
      return "day";
    default:
      return interval;
  }
}

/** Plain text out of a rich-text field. For surfaces whose contract is a
 *  one-line lead (card abstracts, the author page's intro): admin-entered
 *  HTML renders as its text, never as literal tags. */
export function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, "");
}

/**
 * The one place a given and a family name are joined. Every byline, card,
 * alt text and picker label reads through it, so no surface can quietly drop
 * the surname the way the news byline did (B3). Empty in, empty out — the
 * caller decides what an empty name means ("—", null, hide the row).
 */
export function formatPersonName(p: { name?: string | null; surname?: string | null }): string {
  return [p.name, p.surname]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" ");
}
